import type { VisualizationSpec } from "vega-embed";
import { withLabelFit } from "../../../services/chartStudio/applyChartFormatToSpec";
import type {
  ChartCellGrid,
  ChartCellSize,
  ChartLayoutCalibration,
  ResponsiveChartPlan,
} from "./ChartWidget.types";

const DEFAULT_PADDING = { top: 8, bottom: 24, left: 8, right: 8 };

// Composed Vega-Lite specs (facet / concat / repeat) cannot use autosize
// "fit": Vega sizes every plot cell from the child width/height and then adds
// axes, facet headers, titles, legends and gaps around the cells. What those
// surroundings measure is only known after a render, so a chart's first
// layout uses these estimates; each render is then measured
// (chartFitMeasure.ts) and the next layout subtracts the real surroundings,
// which makes the cells fill the card exactly. The preferred width only
// decides how a generic wrapped facet is arranged.
const PREFERRED_FACET_WIDTH_PX = 160;
const FACET_GAP_PX = 16;
const FACET_WIDTH_CHROME_PX = 48;
const FACET_HEIGHT_CHROME_PX = 64;

// Smallest cell that still reads as a chart (axis labels and a rotated axis
// title fit). A trellis with more cells than fit at this size keeps the size
// and the chart area scrolls along that axis — the small-multiples behaviour
// of Power BI and Tableau's "Standard" fit — instead of squeezing every cell
// until the marks and axis titles collide.
export const MIN_FACET_CELL_WIDTH_PX = PREFERRED_FACET_WIDTH_PX;
export const MIN_FACET_CELL_HEIGHT_PX = 80;

const COMPOSITION_KEYS = ["facet", "hconcat", "vconcat", "concat", "repeat"];

/**
 * Top-level param stamping the planned cell size into the compiled view. The
 * measurement reads it back from the view, so it is always paired with the
 * layout that produced it — never with a newer plan whose view is still
 * being built.
 */
export const CHART_FIT_CELL_PARAM = "chartFitCell";

type SpecRecord = Record<string, unknown>;

const asRecord = (value: unknown): SpecRecord | null =>
  typeof value === "object" && value !== null ? (value as SpecRecord) : null;

export const isWidthSizableSpec = (spec: SpecRecord): boolean =>
  "mark" in spec || "layer" in spec;

/** Facet / concat / repeat: a grid of cells whose total size can exceed the card. */
export const isComposedSpec = (spec: SpecRecord): boolean =>
  COMPOSITION_KEYS.some((key) => key in spec);

/**
 * The widget header draws the chart title, so a root `title` would paint a
 * second copy inside the canvas. Only the root goes: a concat panel's own
 * title labels that panel, not the chart.
 */
const withoutChartTitle = (spec: SpecRecord): SpecRecord => {
  if (!("title" in spec)) return spec;
  const next = { ...spec };
  delete next.title;
  return next;
};

const withTooltip = (mark: unknown): unknown => {
  if (typeof mark === "string") return { type: mark, tooltip: true };
  const markObject = asRecord(mark);
  return markObject ? { ...markObject, tooltip: true } : mark;
};

const inlineValues = (spec: SpecRecord): SpecRecord[] => {
  const data = asRecord(spec.data);
  return Array.isArray(data?.values)
    ? data.values.flatMap((value) => asRecord(value) ?? [])
    : [];
};

const facetCardinality = (
  values: readonly SpecRecord[],
  definition: unknown,
): number | null => {
  const field = asRecord(definition)?.field;
  if (typeof field !== "string" || values.length === 0) return null;
  return new Set(values.map((value) => value[field])).size || null;
};

/**
 * One cell's share of the container, never below `minSize`. `measuredChrome`
 * is everything a render showed around the cells (gaps included); before any
 * render the estimated chrome plus the estimated gaps stand in for it.
 */
const cellSize = (
  containerSize: number,
  count: number,
  estimatedChrome: number,
  minSize: number,
  measuredChrome?: number | null,
): number => {
  const chrome =
    measuredChrome ?? estimatedChrome + FACET_GAP_PX * (count - 1);
  return Math.max(minSize, Math.floor((containerSize - chrome) / count));
};

/** Card-derived size for every cell of a `columns` × `rows` grid. */
const gridCellSize = (
  containerWidthPx: number,
  containerHeightPx: number,
  grid: ChartCellGrid,
  calibration?: ChartLayoutCalibration,
): ChartCellSize => ({
  width:
    containerWidthPx > 0
      ? cellSize(
          containerWidthPx,
          grid.columns,
          FACET_WIDTH_CHROME_PX,
          MIN_FACET_CELL_WIDTH_PX,
          calibration?.chromeWidth,
        )
      : null,
  height:
    containerHeightPx > 0
      ? cellSize(
          containerHeightPx,
          grid.rows,
          FACET_HEIGHT_CHROME_PX,
          MIN_FACET_CELL_HEIGHT_PX,
          calibration?.chromeHeight,
        )
      : null,
});

/** The grid Vega draws: a measured one wins over the spec-based prediction. */
const renderedGrid = (
  predicted: ChartCellGrid,
  calibration?: ChartLayoutCalibration,
): ChartCellGrid => ({
  columns: calibration?.columns ?? predicted.columns,
  rows: calibration?.rows ?? predicted.rows,
});

const withCellParam = (spec: SpecRecord, cell: ChartCellSize): SpecRecord => ({
  ...spec,
  params: [
    ...(Array.isArray(spec.params)
      ? spec.params.filter(
          (param) => asRecord(param)?.name !== CHART_FIT_CELL_PARAM,
        )
      : []),
    { name: CHART_FIT_CELL_PARAM, value: cell },
  ],
});

/** Ellipsise column-header labels at the cell width instead of letting a long
 * member name run into its neighbours. A spec's own labelLimit wins. */
const withHeaderLabelLimit = (
  channel: unknown,
  cellWidth: number | null,
): unknown => {
  const definition = asRecord(channel);
  if (!definition || cellWidth === null) return channel;
  return {
    ...definition,
    header: { labelLimit: cellWidth, ...(asRecord(definition.header) ?? {}) },
  };
};

const composedPlan = (
  spec: SpecRecord,
  cell: ChartCellSize,
  grid: ChartCellGrid,
): ResponsiveChartPlan => ({ spec: spec as VisualizationSpec, cell, grid });

function facetPlan(
  spec: SpecRecord,
  containerWidthPx: number,
  containerHeightPx: number,
  padding: SpecRecord,
  calibration?: ChartLayoutCalibration,
): ResponsiveChartPlan {
  const facet = asRecord(spec.facet);
  const child = asRecord(spec.spec);
  if (!facet || !child) {
    return { spec: { ...spec, padding } as VisualizationSpec, cell: null, grid: null };
  }

  const values = inlineValues(spec);
  const isWrappedFacet = typeof facet.field === "string";
  const valueCount = isWrappedFacet
    ? facetCardinality(values, facet)
    : null;
  const requestedColumns =
    typeof spec.columns === "number" && spec.columns > 0
      ? Math.floor(spec.columns)
      : null;
  const fittingColumns =
    containerWidthPx > 0
      ? Math.max(
          1,
          Math.floor(
            (containerWidthPx - FACET_WIDTH_CHROME_PX + FACET_GAP_PX) /
              (PREFERRED_FACET_WIDTH_PX + FACET_GAP_PX),
          ),
        )
      : null;
  const wrappedColumns = valueCount
    ? Math.min(valueCount, requestedColumns ?? fittingColumns ?? valueCount)
    : requestedColumns;

  // Raw distinct values are only a first guess: an aggregated, binned or
  // transformed facet field lays out a different number of cells, which the
  // measured calibration then reports.
  const predicted: ChartCellGrid = isWrappedFacet
    ? {
        columns: wrappedColumns ?? 1,
        rows:
          valueCount && wrappedColumns
            ? Math.ceil(valueCount / wrappedColumns)
            : 1,
      }
    : {
        columns: facet.column ? facetCardinality(values, facet.column) ?? 1 : 1,
        rows: facet.row ? facetCardinality(values, facet.row) ?? 1 : 1,
      };
  // A wrapped facet's column count is this plan's own choice, so it is exact.
  const grid =
    isWrappedFacet && valueCount ? predicted : renderedGrid(predicted, calibration);
  const cell = gridCellSize(
    containerWidthPx,
    containerHeightPx,
    grid,
    calibration,
  );

  return composedPlan(
    {
      ...spec,
      ...(isWrappedFacet && wrappedColumns ? { columns: wrappedColumns } : {}),
      facet: isWrappedFacet
        ? withHeaderLabelLimit(facet, cell.width)
        : facet.column
          ? { ...facet, column: withHeaderLabelLimit(facet.column, cell.width) }
          : facet,
      spec: {
        ...child,
        ...(cell.width !== null ? { width: cell.width } : {}),
        ...(cell.height !== null ? { height: cell.height } : {}),
      },
      padding,
    },
    cell,
    predicted,
  );
}

const childPadding = (spec: SpecRecord): SpecRecord => ({
  ...DEFAULT_PADDING,
  ...(asRecord(spec.padding) ?? {}),
});

/**
 * Resize a nested Vega-Lite view. Nested unit/layer specs accept explicit
 * width/height but not their own autosize block; their composition parent
 * owns layout. Nested compositions recurse until every drawable view receives
 * the card-derived geometry.
 */
function responsiveNestedSpec(
  spec: SpecRecord,
  widthPx: number,
  heightPx: number,
): SpecRecord {
  if ("facet" in spec) {
    return facetPlan(spec, widthPx, heightPx, childPadding(spec))
      .spec as SpecRecord;
  }

  if (
    "hconcat" in spec ||
    "vconcat" in spec ||
    "concat" in spec ||
    "repeat" in spec
  ) {
    return compositionPlan(spec, widthPx, heightPx, childPadding(spec))
      .spec as SpecRecord;
  }

  if (!isWidthSizableSpec(spec)) return spec;
  return {
    ...spec,
    ...(widthPx > 0 ? { width: widthPx } : {}),
    ...(heightPx > 0 ? { height: heightPx } : {}),
    mark: withTooltip(spec.mark),
  };
}

/** Grid of a concat / repeat spec — fully determined by its structure. */
function compositionGrid(spec: SpecRecord): ChartCellGrid | null {
  if (Array.isArray(spec.hconcat)) {
    return { columns: Math.max(1, spec.hconcat.length), rows: 1 };
  }
  if (Array.isArray(spec.vconcat)) {
    return { columns: 1, rows: Math.max(1, spec.vconcat.length) };
  }
  if (Array.isArray(spec.concat)) {
    const columns = Math.max(
      1,
      typeof spec.columns === "number" && spec.columns > 0
        ? Math.floor(spec.columns)
        : spec.concat.length,
    );
    return { columns, rows: Math.max(1, Math.ceil(spec.concat.length / columns)) };
  }
  const repeat = Array.isArray(spec.repeat)
    ? { column: spec.repeat }
    : asRecord(spec.repeat);
  if (!repeat || !asRecord(spec.spec)) return null;
  return {
    columns: Array.isArray(repeat.column) ? Math.max(1, repeat.column.length) : 1,
    rows: Array.isArray(repeat.row) ? Math.max(1, repeat.row.length) : 1,
  };
}

function compositionPlan(
  spec: SpecRecord,
  containerWidthPx: number,
  containerHeightPx: number,
  padding: SpecRecord,
  calibration?: ChartLayoutCalibration,
): ResponsiveChartPlan {
  const predicted = compositionGrid(spec);
  if (!predicted) {
    return { spec: { ...spec, padding } as VisualizationSpec, cell: null, grid: null };
  }
  const cell = gridCellSize(
    containerWidthPx,
    containerHeightPx,
    renderedGrid(predicted, calibration),
    calibration,
  );
  const resize = (child: unknown) =>
    responsiveNestedSpec(asRecord(child) ?? {}, cell.width ?? 0, cell.height ?? 0);

  for (const key of ["hconcat", "vconcat", "concat"] as const) {
    const children = spec[key];
    if (Array.isArray(children)) {
      return composedPlan(
        { ...spec, [key]: children.map(resize), padding },
        cell,
        predicted,
      );
    }
  }
  return composedPlan(
    { ...spec, spec: resize(spec.spec), padding },
    cell,
    predicted,
  );
}

/* ---- Horizontal bars: one readable row per category ---- */

/** A bar row at the theme's 11px labels (Vega-Lite's own default band step). */
const HORIZONTAL_BAR_ROW_PX = 20;
/** The value axis (labels and title) and the default padding around the rows, and a titled top legend when the bars are split by colour. */
const HORIZONTAL_BARS_AXIS_PX = 76;
const HORIZONTAL_BARS_LEGEND_PX = 56;
/** The tallest a horizontal bar chart grows; past it the category labels thin (the theme's parity). */
export const HORIZONTAL_BARS_MAX_HEIGHT_PX = 480;

const DISCRETE_TYPES = ["nominal", "ordinal"];

const markName = (mark: unknown): string | null =>
  typeof mark === "string" ? mark : typeof asRecord(mark)?.type === "string" ? (asRecord(mark)?.type as string) : null;

/** The first unit drawing marks (not a text label layer), with the encoding it inherits from its parents. */
const markEncoding = (spec: SpecRecord, inherited: SpecRecord = {}): SpecRecord | null => {
  const encoding = { ...inherited, ...(asRecord(spec.encoding) ?? {}) };
  const mark = markName(spec.mark);
  if (mark && mark !== "text") return encoding;
  for (const layer of Array.isArray(spec.layer) ? spec.layer : []) {
    const found = markEncoding(asRecord(layer) ?? {}, encoding);
    if (found) return found;
  }
  return null;
};

const distinctCount = (values: readonly SpecRecord[], channel: unknown): number => {
  const field = asRecord(channel)?.field;
  return typeof field === "string" ? new Set(values.map((value) => value[field])).size : 1;
};

/**
 * The height a horizontal chart (categories down y, values or times across x: bars, lollipops,
 * dots, dumbbells…) reads at, read from its encoding: a row per category, side-by-side series included, plus its axis and legend, up to the cap. `null` for
 * any other chart, or one whose rows aren't inline.
 */
export function horizontalBarsHeightPx(spec: VisualizationSpec): number | null {
  const source = spec as SpecRecord;
  if (!isWidthSizableSpec(source)) return null;
  const encoding = markEncoding(source);
  const y = asRecord(encoding?.y);
  const xType = asRecord(encoding?.x)?.type;
  if (!encoding || !y || !DISCRETE_TYPES.includes(y.type as string) || typeof xType !== "string" || DISCRETE_TYPES.includes(xType)) return null;
  const values = inlineValues(source);
  if (values.length === 0) return null;
  const rows = distinctCount(values, y) * (encoding.yOffset ? distinctCount(values, encoding.yOffset) : 1);
  const legend = encoding.color ? HORIZONTAL_BARS_LEGEND_PX : 0;
  return Math.min(HORIZONTAL_BARS_MAX_HEIGHT_PX, rows * HORIZONTAL_BAR_ROW_PX + HORIZONTAL_BARS_AXIS_PX + legend);
}

/**
 * Fits every Vega-Lite shape to the measured plot box, without the root title
 * (the widget header shows it). Unit/layer specs use
 * autosize; facet, concat, and repeat specs receive explicit cell dimensions
 * because Vega-Lite cannot fit those compositions itself. `calibration` is the
 * previous render's measurement of the same chart (see chartFitMeasure.ts):
 * with it the cells fill the box exactly, without it they use estimates.
 */
export function planResponsiveChart(
  spec: VisualizationSpec,
  containerWidthPx = 0,
  containerHeightPx = 0,
  calibration?: ChartLayoutCalibration,
): ResponsiveChartPlan {
  const source = withoutChartTitle(spec as SpecRecord);
  const existingPadding = asRecord(source.padding) ?? {};
  const padding = { ...DEFAULT_PADDING, ...existingPadding };

  const composed =
    "facet" in source
      ? facetPlan(
          source,
          containerWidthPx,
          containerHeightPx,
          padding,
          calibration,
        )
      : "hconcat" in source ||
          "vconcat" in source ||
          "concat" in source ||
          "repeat" in source
        ? compositionPlan(
            source,
            containerWidthPx,
            containerHeightPx,
            padding,
            calibration,
          )
        : null;
  if (composed) {
    return composed.cell
      ? {
          ...composed,
          spec: withCellParam(
            composed.spec as SpecRecord,
            composed.cell,
          ) as VisualizationSpec,
        }
      : composed;
  }

  if (!isWidthSizableSpec(source)) {
    return { spec: { ...source, padding } as VisualizationSpec, cell: null, grid: null };
  }

  const hasMeasuredWidth = containerWidthPx > 0;
  // Horizontal bars take a row per category: a content-sized card grows to it, a fixed one scrolls past its box.
  const barsHeight = horizontalBarsHeightPx(source as VisualizationSpec);
  const height = barsHeight !== null ? Math.max(barsHeight, containerHeightPx) : containerHeightPx;
  const hasMeasuredHeight = height > 0;
  return {
    spec: {
      ...(hasMeasuredWidth ? (withLabelFit(source as VisualizationSpec, containerWidthPx) as SpecRecord) : source),
      width: hasMeasuredWidth ? containerWidthPx : "container",
      ...(hasMeasuredHeight ? { height } : {}),
      ...(hasMeasuredWidth || hasMeasuredHeight
        ? {
            autosize: {
              type: hasMeasuredHeight ? "fit" : "fit-x",
              contains: "padding",
            },
          }
        : {}),
      mark: withTooltip(source.mark),
      padding,
    } as VisualizationSpec,
    cell: null,
    grid: null,
  };
}

/** The spec half of {@link planResponsiveChart}. */
export function toResponsiveSpec(
  spec: VisualizationSpec,
  containerWidthPx = 0,
  containerHeightPx = 0,
  calibration?: ChartLayoutCalibration,
): VisualizationSpec {
  return planResponsiveChart(
    spec,
    containerWidthPx,
    containerHeightPx,
    calibration,
  ).spec;
}
